import React, { useEffect, useMemo, useState } from "react";
import { api, fmtOdds } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { TeamLogo } from "@/components/TeamLogo";
import { LeagueChip } from "@/components/LeagueLogo";
import { Store, Sparkles, ChevronRight, Award, Loader2 } from "lucide-react";

const LEAGUES = ["All", "NBA", "NFL", "NHL", "MLB", "NCAAF", "NCAAB", "EPL"];

export default function Bookmakers() {
  const [league, setLeague] = useState("All");
  const [games, setGames] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const params = league === "All" ? {} : { league };
    api.get("/games", { params })
      .then((r) => {
        setGames(r.data);
        if (!selected && r.data.length) setSelected(r.data[0].id);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [league]);

  useEffect(() => {
    if (!selected) return;
    setDetailLoading(true);
    api.get(`/bookmakers/game/${selected}`)
      .then((r) => setDetail(r.data))
      .finally(() => setDetailLoading(false));
  }, [selected]);

  return (
    <div className="mx-auto max-w-7xl space-y-6" data-testid="bookmakers-page">
      <PageHeader
        title="Line shop"
        subtitle="Compare every US bookmaker side-by-side for each game. Best price per market is highlighted — that's where your edge lives."
        actions={
          <div className="flex items-center gap-1 rounded-lg border border-border bg-gray-0 p-1 overflow-x-auto no-scrollbar">
            {LEAGUES.map((l) => (
              <button
                key={l}
                onClick={() => setLeague(l)}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-all whitespace-nowrap ${
                  league === l ? "bg-primary text-primary-foreground" : "text-gray-500 hover:text-gray-900"
                }`}
                data-testid={`bk-league-${l.toLowerCase()}`}
              >
                {l}
              </button>
            ))}
          </div>
        }
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[320px,1fr]">
        {/* Left: game list */}
        <div className="iso-card overflow-hidden">
          <div className="border-b border-border bg-gray-50 px-4 py-3">
            <div className="font-lexend text-xs font-bold text-gray-900">Upcoming games</div>
            <div className="text-[10px] text-gray-500">{games.length} with odds</div>
          </div>
          <div className="max-h-[620px] overflow-y-auto custom-scrollbar">
            {loading && (
              <div className="flex items-center justify-center p-8 text-gray-500">
                <Loader2 className="mr-2 size-4 animate-spin" /> Loading…
              </div>
            )}
            {games.map((g) => (
              <button
                key={g.id}
                onClick={() => setSelected(g.id)}
                className={`flex w-full items-center gap-2.5 border-b border-border px-4 py-3 text-left transition-colors last:border-b-0 ${
                  selected === g.id ? "bg-secondary-lighter/60" : "hover:bg-gray-50"
                }`}
                data-testid={`bk-game-${g.id}`}
              >
                <TeamLogo src={g.away_logo} abbr={g.away_team_abbr} size={24} />
                <TeamLogo src={g.home_logo} abbr={g.home_team_abbr} size={24} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-xs font-semibold text-gray-900">
                    {g.away_team_abbr} @ {g.home_team_abbr}
                  </div>
                  <div className="mt-0.5 flex items-center gap-1.5 text-[10px] text-gray-500">
                    <LeagueChip code={g.league} className="!text-[10px]" />
                    <span>{new Date(g.start_time).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
                  </div>
                </div>
                <ChevronRight className={`size-3.5 ${selected === g.id ? "text-secondary-dark" : "text-gray-300"}`} />
              </button>
            ))}
          </div>
        </div>

        {/* Right: comparison matrix */}
        <div>
          {detailLoading && !detail && (
            <div className="iso-card p-12 text-center text-sm text-gray-500">
              <Loader2 className="mx-auto mb-2 size-5 animate-spin" /> Loading bookmakers…
            </div>
          )}
          {detail && <ComparisonTable detail={detail} />}
          {!detailLoading && !detail && (
            <div className="iso-card p-12 text-center text-sm text-gray-500">
              <Store className="mx-auto mb-3 size-8 text-gray-300" />
              Select a game to compare bookmakers.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const ComparisonTable = ({ detail }) => {
  const { bookmakers = [], best = {}, matchup, home_team_abbr, away_team_abbr, home_logo, away_logo, league } = detail;

  const isBest = (field, book, value) =>
    best[field] && best[field].book === book.title && best[field].value === value;

  // Summary edge: how many cents/points better the best line is vs the worst
  const edge = useMemo(() => {
    if (!bookmakers.length) return null;
    const odds = bookmakers.map((b) => b.moneyline_home).filter((v) => v != null);
    if (odds.length < 2) return null;
    const maxV = Math.max(...odds);
    const minV = Math.min(...odds);
    return { max: maxV, min: minV, diff: maxV - minV };
  }, [bookmakers]);

  return (
    <div className="iso-card overflow-hidden" data-testid="bookmakers-comparison">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-gray-50 px-5 py-4">
        <div className="flex items-center gap-3">
          <TeamLogo src={away_logo} abbr={away_team_abbr} size={32} />
          <TeamLogo src={home_logo} abbr={home_team_abbr} size={32} />
          <div>
            <div className="font-lexend text-sm font-bold text-gray-900">{matchup}</div>
            <div className="mt-0.5 flex items-center gap-1.5 text-[10px] text-gray-500">
              <LeagueChip code={league} className="!text-[10px]" />
              <span>{bookmakers.length} books</span>
            </div>
          </div>
        </div>
        {edge && (
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Sparkles className="size-3.5 text-secondary" />
            Best-line edge: <span className="font-lexend font-bold text-gray-900">{edge.diff > 0 ? "+" : ""}{edge.diff} pts</span>
            <span className="text-gray-400">(home ML)</span>
          </div>
        )}
      </div>

      <div className="overflow-x-auto custom-scrollbar">
        <table className="w-full text-xs" data-testid="bookmakers-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-gray-400">
              <th className="px-4 py-3 font-semibold">Bookmaker</th>
              <th className="px-3 py-3 text-right font-semibold">{away_team_abbr} ML</th>
              <th className="px-3 py-3 text-right font-semibold">{home_team_abbr} ML</th>
              <th className="px-3 py-3 text-right font-semibold">{away_team_abbr} Spread</th>
              <th className="px-3 py-3 text-right font-semibold">{home_team_abbr} Spread</th>
              <th className="px-3 py-3 text-right font-semibold">Over</th>
              <th className="px-3 py-3 text-right font-semibold">Under</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {bookmakers.map((b, i) => (
              <tr key={b.key || i} className="hover:bg-gray-50" data-testid={`book-row-${b.key || i}`}>
                <td className="px-4 py-2.5 font-semibold text-gray-900 whitespace-nowrap">{b.title}</td>
                <OddsCell best={isBest("moneyline_away", b, b.moneyline_away)} value={b.moneyline_away} />
                <OddsCell best={isBest("moneyline_home", b, b.moneyline_home)} value={b.moneyline_home} />
                <OddsCell
                  best={isBest("spread_price_away", b, b.spread_price_away)}
                  value={b.spread_price_away}
                  line={b.spread != null ? -b.spread : null}
                />
                <OddsCell
                  best={isBest("spread_price_home", b, b.spread_price_home)}
                  value={b.spread_price_home}
                  line={b.spread}
                />
                <OddsCell
                  best={isBest("total_over_price", b, b.total_over_price)}
                  value={b.total_over_price}
                  line={b.total}
                  linePrefix="O"
                />
                <OddsCell
                  best={isBest("total_under_price", b, b.total_under_price)}
                  value={b.total_under_price}
                  line={b.total}
                  linePrefix="U"
                />
              </tr>
            ))}
            {bookmakers.length === 0 && (
              <tr>
                <td colSpan={7} className="p-8 text-center text-gray-500">
                  No bookmaker data for this game. Run an admin refresh.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Best lines summary */}
      {Object.keys(best).length > 0 && (
        <div className="border-t border-border bg-gray-50 px-5 py-4">
          <div className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
            <Award className="size-3.5 text-green" /> Best lines
          </div>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-6">
            {[
              ["moneyline_away", `${away_team_abbr} ML`],
              ["moneyline_home", `${home_team_abbr} ML`],
              ["spread_price_away", `${away_team_abbr} Sp`],
              ["spread_price_home", `${home_team_abbr} Sp`],
              ["total_over_price", "Over"],
              ["total_under_price", "Under"],
            ].map(([f, label]) => {
              const b = best[f];
              if (!b) return null;
              return (
                <div key={f} className="rounded-lg bg-gray-0 px-3 py-2 border border-border">
                  <div className="text-[9px] font-semibold uppercase tracking-wider text-gray-400">{label}</div>
                  <div className="flex items-center justify-between">
                    <div className="font-lexend text-sm font-bold text-gray-900">{fmtOdds(b.value)}</div>
                    <div className="text-[9px] font-medium text-green-dark truncate ml-2">{b.book}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const OddsCell = ({ best, value, line, linePrefix }) => {
  if (value == null) {
    return <td className="px-3 py-2.5 text-right text-gray-300">—</td>;
  }
  return (
    <td className={`px-3 py-2.5 text-right ${best ? "bg-green-lighter/40" : ""}`}>
      <div className="flex flex-col items-end">
        {line != null && (
          <div className="text-[9px] text-gray-400">
            {linePrefix ? `${linePrefix} ${line}` : `${line > 0 ? "+" : ""}${line}`}
          </div>
        )}
        <div className={`font-lexend text-xs font-bold ${best ? "text-green-dark" : "text-gray-900"}`}>
          {fmtOdds(value)}
          {best && <span className="ml-1 rounded-sm bg-green px-1 py-0.5 text-[8px] font-bold text-white align-middle">BEST</span>}
        </div>
      </div>
    </td>
  );
};
